import { loadProfile } from '@/embed/cache'
import { CallbackWrap } from '@/types/common'
import { CraftingEntry, Embed } from '@/types/embed'
import { LevelingEmbed, LevelingOverview } from '@/types/leveling'
import { faEdit, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ExternalLink, Select, Tag } from '@maxroll-media-group/common-libs'
import { CraftingProfile, FrontendPath } from 'common'
import { EmbedRenderer } from 'components/editor/rich-text-editor/utils/EmbedRenderer'
import { Widget } from 'leveling/widget'
import React from 'react'
import './crafting-guides.scss'

type LevelingCrafting = Exclude<LevelingOverview['craftingGuides'], undefined>[0]

export function CraftingGuides({
  embed,
  onEmbedChange
}: {
  embed: LevelingEmbed
  onEmbedChange?: (value: CallbackWrap<Embed>) => void
}) {
  const [craftingItems, setCraftingItems] = React.useState<CraftingEntry[]>([])

  React.useEffect(() => {
    const getCraftingItems = async () => {
      const profile = await loadProfile(CraftingProfile)
      for (const embed of profile.embeds) {
        if (embed.id === 1 && embed.type === 'crafting') {
          return embed.items
        }
      }
    }

    let current = true
    getCraftingItems().then((data) => {
      if (current && data) setCraftingItems(data)
    })
    return () => {
      current = false
    }
  }, [])

  const content = embed?.overview?.craftingGuides || []

  // For testing with all items:
  // const content = craftingItems.map((item) => ({ id: item.link })) || []

  const [isEditing, setIsEditing] = React.useState(false)

  const onAdd = React.useCallback(
    () =>
      onEmbedChange?.((embed) => {
        if (embed.type !== 'leveling') return embed
        embed = { ...embed }
        embed.overview = { ...embed.overview }
        embed.overview.craftingGuides = embed.overview.craftingGuides?.slice() ?? []
        embed.overview.craftingGuides.push({ id: '' })
        return embed
      }),
    [onEmbedChange]
  )

  const onDelete = (index: number) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      embed = { ...embed }
      embed.overview = { ...embed.overview }
      embed.overview.craftingGuides = embed.overview.craftingGuides?.slice() ?? []
      embed.overview.craftingGuides.splice(index, 1)
      return embed
    })

  const onEditAttr = <K extends keyof LevelingCrafting>(index: number, attr: K, newValue: LevelingCrafting[K]) =>
    onEmbedChange?.((embed) => {
      if (embed.type !== 'leveling') return embed
      embed = { ...embed }
      embed.overview = { ...embed.overview }
      embed.overview.craftingGuides = embed.overview.craftingGuides?.slice() ?? []
      embed.overview.craftingGuides[index] = {
        ...embed.overview.craftingGuides[index],
        [attr]: newValue
      }
      return embed
    })

  const craftingItemOptions = React.useMemo(() => {
    return craftingItems.map((item) => ({
      value: item.link,
      label: item.name
    }))
  }, [craftingItems])

  return (Array.isArray(content) && content.length > 0) || onEmbedChange ? (
    <Widget
      className="poe-crafting-guides"
      title={
        <>
          Crafting Guides
          {onEmbedChange &&
            (isEditing ? (
              <div>
                <button onClick={() => setIsEditing(false)}>Close</button>
              </div>
            ) : (
              <div>
                <FontAwesomeIcon
                  icon={faEdit}
                  onClick={() => {
                    setIsEditing(!isEditing)
                  }}
                />
              </div>
            ))}
        </>
      }>
      <div className="poe-crafting-items">
        {isEditing ? (
          <div className="poe-crafting-items-edit">
            {content.map((item, index) => (
              <div key={index}>
                <Select
                  className="poe-commons-lib-select"
                  options={craftingItemOptions}
                  required
                  placeholder="Crafting Guide"
                  value={item.id}
                  onChange={(newValue) => {
                    if (newValue) onEditAttr(index, 'id', newValue.value)
                  }}
                />
                <FontAwesomeIcon
                  icon={faTrashAlt}
                  onClick={() => {
                    onDelete(index)
                  }}
                />
              </div>
            ))}
            <button onClick={onAdd}>Add</button>
          </div>
        ) : (
          content.map((item, index) => {
            const hit = craftingItems.find((craftingItem) => craftingItem.link === item.id)
            if (!hit) return null

            return (
              <div key={index} className="poe-crafting-item">
                <div className="poe-crafting-item-image-container">
                  {hit.itemId != null && (
                    <EmbedRenderer
                      type="poe-item"
                      id={hit.itemId}
                      profile={CraftingProfile}
                      options={{ preview: '1' }}
                      text={hit.name}
                    />
                  )}
                </div>

                <ExternalLink to={`${FrontendPath}${hit.link}`} className="poe-crafting-item-content-overlay">
                  <Tag tagStyle="default" tagColor="blue">
                    Crafting Guides
                  </Tag>

                  <div className="poe-crafting-item-content">
                    <div className="poe-crafting-item-top">
                      <h2 className="poe-crafting-item-title" title={hit.name}>
                        {hit.name}
                      </h2>

                      {/* {hit?.post_author?.display_name ? (
                        <span className={styles.details}>
                          <span>by {post.post_author.display_name}</span>
                          <span className={styles.date}>
                            {formatDate(post.post_modified_formatted)}
                          </span>
                        </span>
                      ) : null} */}
                    </div>
                  </div>
                </ExternalLink>
              </div>
              // <ExternalLink
              //   key={index}
              //   to={`${FrontendPath}${hit.link}`}
              //   className="poe-crafting-items-item">
              //   <div
              //     style={{
              //       backgroundImage: `url('${BaseAssetPath}/images/pattern-01.webp')`
              //     }}
              //     className="poe-pattern"></div>

              //   <div className="poe-image-wrapper">
              //     {hit.itemId != null && (
              //       <EmbedRenderer
              //         type="poe-item"
              //         id={hit.itemId}
              //         profile={CraftingProfile}
              //         options={{ preview: '1' }}
              //         text={hit.name}
              //       />
              //     )}
              //   </div>

              //   <div className="poe-details">
              //     <div>
              //       <h2 title={hit.name}>{hit.name}</h2>
              //     </div>
              //   </div>
              // </ExternalLink>
            )
          })
        )}
      </div>
    </Widget>
  ) : null
}
